import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getServiceNameWithNickname } from "../../../utils/getServiceNameWithNickname";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Notification from "../notification/Notification";
import removeDuplicateFromArrayOfObjectByField from "../../../utils/removeDuplicateFromArrayOfObjectByField";
import { HTTP_STATUSES, PAYMENT_SYSTEM, STANDARD_ERRORS_WAY } from "../../../utils/consts";
import { removeTransferFromCollection } from "../../../utils/collections";
import { useTranslation } from "react-i18next";
import { parseAxiosError } from "../../../utils/response";
import FormHelperText from "@material-ui/core/FormHelperText";

const ServiceRelationshipsSelect = ({
  project,
  setCheckedServiceRelationships,
  isObject = true,
  defaultOption = false,
  balanceRefiling = true,
  errors = null,
  clearError = null,
  fieldRefs = null
}) => {
  const { t } = useTranslation("otherInterface");
  const [service, setService] = useState(null);
  const [paymentSystems, setPaymentSystems] = useState(null);
  const [currencies, setCurrencies] = useState(null);

  const [checkedService, setCheckedService] = useState(null);
  const [checkedPaymentSystems, setCheckedPaymentSystems] = useState(null);
  const [checkedCurrency, setCheckedCurrency] = useState(null);

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const fetchServiceRelationshipsByServiceName = (data) => {
    axios.get("/api/service-relationships?service.name=" + data.name, userAuthenticationConfig()).then(response => {
      if (response.status === 200) {
        let currency = [];

        response.data["hydra:member"].forEach(item => {
          currency.push(item.currency);
        });

        setCurrencies(removeDuplicateFromArrayOfObjectByField(currency, "asset"));
        setPaymentSystems(null);

        setCheckedServiceRelationships({
          "service": data,
          "currency": null,
          "paymentSystem": null
        });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchServiceRelationshipsByCurrencyAsset = (data) => {
    axios.get("/api/service-relationships?service.name=" + checkedService.name + "&currency.asset=" + data.asset,
      userAuthenticationConfig()).then(response => {
      if (response.status === 200) {
        let paymentSystems = [];

        response.data["hydra:member"].forEach(item => {
          if (!balanceRefiling && item.paymentSystem.subName === PAYMENT_SYSTEM.BALANCE_REFILING) {
            return;
          }
          paymentSystems.push(item.paymentSystem);
        });

        setPaymentSystems(removeTransferFromCollection(paymentSystems));

        setCheckedServiceRelationships({
          "service": checkedService,
          "currency": data,
          "paymentSystem": null
        });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchServiceByProjectName = () => {
    axios.get("/api/connections/list-for-invoice/" + project.id, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK && "hydra:member" in response.data) {
        let service = [];

        response.data["hydra:member"].forEach(item => {
          service.push(item.service);
        });

        service = removeTransferFromCollection(service);

        setService(service);

        setCurrencies(null);
        setPaymentSystems(null);

        setCheckedServiceRelationships({
          "service": null,
          "currency": null,
          "paymentSystem": null
        });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  useEffect(() => {
    if (!project) {
      return;
    }

    fetchServiceByProjectName();
  }, [project]);

  const handleChangePaymentSystemSelect = (data) => {
    setCheckedPaymentSystems(data);

    setCheckedServiceRelationships({
      "service": checkedService,
      "currency": checkedCurrency,
      "paymentSystem": data
    });
    clearError && clearError("paymentSystem");
  };

  const handleChangeCurrencySelect = (currency) => {
    setCheckedCurrency(currency);
    fetchServiceRelationshipsByCurrencyAsset(currency);
    clearError && clearError("currency");
  };

  const handleChangeServiceSelect = (data) => {
    setCheckedService(data);
    fetchServiceRelationshipsByServiceName(data);
    clearError && clearError("service");
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      {project &&
        <>
          <FormControl error={errors?.service} ref={fieldRefs?.service}>
            <Autocomplete
              options={(isObject ? service : service.map(service => service.name)) || []}
              value={checkedService || null}
              onChange={(event, checkedService) => handleChangeServiceSelect(checkedService)}
              getOptionLabel={(option) => isObject ? getServiceNameWithNickname(option.name, option.nickname) : option}
              renderInput={(params) => (
                <TextField {...params} label={t("serviceReletionships.service")} />
              )}
              blurOnSelect
              disableClearable={!defaultOption}
            />
            {errors?.service && <FormHelperText>{errors?.service}</FormHelperText>}
          </FormControl>
        </>
      }
      {currencies && Object.keys(currencies).length !== 0 && (() => {
        const options = currencies.map((currency) => {
          const firstLetter = currency.asset[0].toUpperCase();
          return {
            firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
            ...currency,
          };
        }).sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));

        return (
          <>
            <FormControl error={errors?.currency} ref={fieldRefs?.currency}>
              <Autocomplete
                options={options}
                groupBy={(option) => option.firstLetter}
                getOptionLabel={(option) => option.asset}
                value={options.find((option) => option.asset === checkedCurrency?.asset) ?? null}
                onChange={(event, currency) => handleChangeCurrencySelect(currency)}
                renderInput={(params) => (
                  <TextField {...params} label={t("serviceReletionships.currency")} />
                )}
                disableClearable
              />
              {errors?.currency && <FormHelperText>{errors.currency}</FormHelperText>}
            </FormControl>
          </>
        );
      })()}
      {paymentSystems && Object.keys(paymentSystems).length !== 0 &&
        <>
          <FormControl error={errors?.paymentSystem} ref={fieldRefs?.paymentSystem}>
            <Autocomplete
              options={(isObject ? paymentSystems : paymentSystems.map(paymentSystem => paymentSystem.name)) || []}
              value={checkedPaymentSystems || null}
              onChange={(event, checkedPaymentSystem) => handleChangePaymentSystemSelect(checkedPaymentSystem)}
              getOptionLabel={(option) => isObject ? option.name : option}
              renderInput={(params) => (
                <TextField {...params} label={t("serviceReletionships.paymentSystem")} />
              )}
              blurOnSelect
              disableClearable={!defaultOption}
            />
            {errors?.paymentSystem && <FormHelperText>{errors.paymentSystem}</FormHelperText>}
          </FormControl>
        </>
      }
    </>
  );
};

export default ServiceRelationshipsSelect;